// ─────────────────────────────────────────────────────────────────────────────
// AVDASH design tokens — SOURCE UNIQUE des couleurs et polices de l'application.
//
// Couleurs de marque issues du logo :
//   bleu  #1D5BA8 (lettrage « AVDASH »)  → colors.brand[600]
//   vert  #7FC8BD (flèche et barres)      → colors.accent[300]
//
// Règles :
//   • Interface : uniquement brand (bleu), accent (vert), neutral (gris ardoise),
//     + danger / warning pour les états.
//   • Graphiques : CATEGORICAL (ordre fixe, validé daltonisme), jamais pour l'UI.
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  brand: {
    50: "#EEF4FB",
    100: "#D7E5F5",
    200: "#B0CBEB",
    300: "#7DA8D9",
    400: "#4A86C7",
    500: "#2077BC",
    600: "#1D5BA8",
    700: "#184B8C",
    800: "#133B6E",
    900: "#0D2A50",
  },
  accent: {
    50: "#EDF8F6",
    100: "#D3EFEA",
    200: "#A9DFD7",
    300: "#7FC8BD",
    400: "#4DB3A5",
    500: "#00A08C",
    600: "#00806F",
    700: "#006357",
    800: "#004A41",
  },
  neutral: {
    0: "#FFFFFF",
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172A",
  },
  danger: {
    50: "#FDECEC",
    100: "#F9D4D4",
    500: "#D64545",
    600: "#B83838",
    700: "#8F2B2B",
  },
  warning: {
    50: "#FEF6E6",
    100: "#FCE7BF",
    500: "#D98E04",
    600: "#B07303",
    700: "#8A5A02",
  },
};

/**
 * Couleurs des séries de graphiques — ordre FIXE (identité d'une série = son rang
 * d'apparition, jamais recyclé). Validé : daltonisme ΔE ≥ 11.8, vision normale ΔE ≥ 16.
 * Les slots 5 et 6 (< 3:1 sur blanc) exigent des libellés visibles.
 */
export const CATEGORICAL: string[] = [
  "#2077BC", // bleu logo
  "#00A08C", // vert logo (saturé pour rester lisible en graphique)
  "#EB6834", // orange
  "#6250D6", // violet
  "#E87BA4", // rose
  "#EDA100", // jaune
];

export const FONTS = {
  /** Titres — géométrique, proche du logotype */
  heading: "Poppins, Inter, system-ui, sans-serif",
  /** Texte et interface */
  body: "Inter, system-ui, -apple-system, sans-serif",
};

/** Alias historique (anciennes clés) — valeurs reliées aux tokens ci-dessus. */
export const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[600],
  roseMed: colors.accent[100],
  roseLight: colors.accent[50],
  roseSoft: colors.neutral[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  successLight: colors.accent[50],
  teal: colors.accent[500],
  tealLight: colors.accent[100],
  orange: colors.warning[600],
  orangeLight: colors.warning[50],
  indigo: colors.brand[600],
  indigoLight: colors.brand[100],
  indigoDark: colors.brand[600],
  indigoMed: colors.brand[200],
};

/** Couleurs des éléments d'analyse (chips, séries des graphiques des vues) — palette d'origine. */
export const CHIP_PALETTE = [
  "#2B5CE6",
  "#3ECFBF",
  "#0D9488",
  "#D97706",
  "#1D4ED8",
  "#EA580C",
  "#2B5CE6",
  "#0D8A80",
  "#047857",
  "#B45309",
  "#1E40AF",
  "#C2410C",
  "#1D4ED8",
  "#0E7490",
  "#065F46",
  "#92400E",
];
